import React, { useState, useEffect } from 'react';
import { RefreshCw, Calendar, AlertTriangle, X, XCircle } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import {
  useCocinaRegistros,
  GrupoReceta,
  RegistroCocina,
} from '../hooks/useCocinaRegistros';
import { cocinaService } from '../services/cocinaService';
import VistaCocinaSegmentada from '../components/cocina/VistaCocinaSegmentada';
import { PedidosAPI } from '../services/pedidosAPI';
import { PedidoAPI } from '../types/pedidos';
import { formatearFechaLarga } from '../utils/dateUtils';
import { useToast } from '../hooks/useToast';

export default function CocinaRegistros() {
  const {
    grupos,
    loading,
    error,
    fechaPreparacion,
    actualizandoEstado,
    cargarDatosProduccion,
    actualizarEstadoRegistro,
    revertirEstadoRegistro,
    transicionarEstadoRegistro,
    // actualizarEstadoMasivo intencionalmente NO consumido (TD-043
    // RESUELTA): el frontend itera el endpoint individual por cada
    // registro del grupo para que cada movimiento de stock quede
    // trazable y reversible. El hook expone el masivo por
    // compatibilidad histórica.
    cambiarFechaPreparacion,
    obtenerTextoBotonRegistro,
    obtenerEstiloBotonRegistro,
    obtenerProximoEstado,
    obtenerIconoProximoEstado,
    gruposVencidos,
    vistaPorSegmento,
  } = useCocinaRegistros();

  const { success: toastOk, error: toastError } = useToast();

  const [gruposExpandidos, setGruposExpandidos] = useState<Set<number>>(
    new Set()
  );

  // Expandir automáticamente grupos con múltiples pedidos.
  // Fix congelamiento (2026-07-03): antes creaba un Set nuevo en cada
  // cambio de `grupos` aunque el contenido fuera idéntico, disparando
  // un rerender extra en cada mutación. Ahora solo actualizamos si el
  // Set nuevo difiere del actual (por tamaño o por ids). Combinado con
  // el startTransition del hook, elimina la cascada de rerenders
  // pesados que congelaba el hilo principal en tab Vencidos.
  useEffect(() => {
    const gruposConMultiplesPedidos = new Set<number>();
    grupos.forEach((grupo) => {
      if (grupo.total_registros > 1) {
        gruposConMultiplesPedidos.add(grupo.receta_id);
      }
    });
    setGruposExpandidos((prev) => {
      if (prev.size !== gruposConMultiplesPedidos.size) {
        return gruposConMultiplesPedidos;
      }
      for (const id of gruposConMultiplesPedidos) {
        if (!prev.has(id)) return gruposConMultiplesPedidos;
      }
      return prev;
    });
  }, [grupos]);
  const [registrosSeleccionados, setRegistrosSeleccionados] = useState<{
    [grupoId: number]: Set<number>;
  }>({});

  // Estado para pestañas de filtrado por estado. La pestaña 'vencidos'
  // muestra pedidos cuya fecha_entrega ya pasó (vienen del endpoint
  // separado /cocina/pedidos-vencidos) — separación puramente visual,
  // el estado del pedido en BD no cambia.
  type TabCocina = 'pendientes' | 'en_preparacion' | 'completadas' | 'todas' | 'vencidos';
  const [tabActivo, setTabActivo] = useState<TabCocina>('pendientes');

  // Tras una transición de estado, mover el tab activo al tab del nuevo
  // estado para que el usuario VEA el cambio. Sin esto, el registro
  // "desaparece" del tab actual (filtro por estado) y el feedback es
  // sólo el toast — el usuario percibe que "el botón no hizo nada".
  // Bug reportado 2026-06-16: chef/personal_cocina no veían reflejados
  // sus clicks en Iniciar/Terminar/Despacho aunque la BD sí cambiaba.
  const tabParaEstado = (estado: string): TabCocina => {
    switch (estado) {
      case 'pendiente':
        return 'pendientes';
      case 'en_preparacion':
        return 'en_preparacion';
      case 'terminado':
      case 'entregado_a_despacho':
      case 'entregado_al_cliente':
        return 'completadas';
      default:
        return 'todas';
    }
  };

  // Estados para modal de advertencia de ingredientes
  const [mostrarModalAdvertencia, setMostrarModalAdvertencia] = useState(false);
  const [
    grupoConIngredientesInsuficientes,
    setGrupoConIngredientesInsuficientes,
  ] = useState<GrupoReceta | null>(null);

  // Estados para modal de detalle de pedido
  const [pedidoDetalle, setPedidoDetalle] = useState<PedidoAPI | null>(null);
  const [mostrarDetallePedido, setMostrarDetallePedido] = useState(false);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);

  // Función para abrir el detalle de un pedido
  const verDetallePedido = async (pedidoId: number) => {
    try {
      setCargandoDetalle(true);
      const pedidosAPI = new PedidosAPI();
      const response = await pedidosAPI.obtener(String(pedidoId));
      // La respuesta del backend tiene estructura { success: true, data: {...} }
      // Necesitamos extraer el objeto data
      const pedido = (response as any).data || response;
      setPedidoDetalle(pedido);
      setMostrarDetallePedido(true);
    } catch (error) {
      console.error('Error al cargar detalle del pedido:', error);
    } finally {
      setCargandoDetalle(false);
    }
  };

  // Togglear expansión de grupo
  const toggleGrupo = (grupoId: number) => {
    const nuevosExpandidos = new Set(gruposExpandidos);
    if (nuevosExpandidos.has(grupoId)) {
      nuevosExpandidos.delete(grupoId);
    } else {
      nuevosExpandidos.add(grupoId);
    }
    setGruposExpandidos(nuevosExpandidos);
  };

  // Manejar selección de registros para acciones masivas
  const toggleSeleccionRegistro = (grupoId: number, registroId: number) => {
    setRegistrosSeleccionados((prev) => {
      const nuevoState = { ...prev };
      if (!nuevoState[grupoId]) {
        nuevoState[grupoId] = new Set();
      }

      const grupo = nuevoState[grupoId];
      if (grupo.has(registroId)) {
        grupo.delete(registroId);
      } else {
        grupo.add(registroId);
      }

      return nuevoState;
    });
  };

  // Seleccionar todos los registros de un grupo
  const toggleSeleccionTodoGrupo = (grupo: GrupoReceta) => {
    const grupoId = grupo.receta_id;
    const seleccionados = registrosSeleccionados[grupoId] || new Set();
    const todosSeleccionados =
      grupo.registros.length > 0 &&
      grupo.registros.every((r) => seleccionados.has(r.id));

    setRegistrosSeleccionados((prev) => ({
      ...prev,
      [grupoId]: todosSeleccionados
        ? new Set()
        : new Set(grupo.registros.map((r) => r.id)),
    }));
  };

  // Calcular ingredientes necesarios para un registro individual
  const calcularIngredientesPorRegistro = (
    grupo: GrupoReceta,
    cantidadPorciones: number
  ) => {
    return grupo.ingredientesTotales.map((ing) => ({
      ...ing,
      cantidad_necesaria_registro: ing.cantidad_por_porcion * cantidadPorciones,
      suficiente_para_registro:
        ing.stock_disponible >= ing.cantidad_por_porcion * cantidadPorciones,
    }));
  };

  // Validar si hay ingredientes suficientes para un registro específico
  const validarStockParaRegistro = (
    grupo: GrupoReceta,
    cantidadPorciones: number
  ): boolean => {
    const ingredientes = calcularIngredientesPorRegistro(
      grupo,
      cantidadPorciones
    );
    return ingredientes.every((ing) => ing.suficiente_para_registro);
  };

  // Manejar click en botón de registro individual
  const manejarClickRegistro = async (
    registro: RegistroCocina,
    grupo: GrupoReceta
  ) => {
    const proximoEstado = obtenerProximoEstado(registro.estado);
    if (!proximoEstado) return;

    // Si va a iniciar preparación, validar stock para este registro específico
    if (proximoEstado === 'en_preparacion') {
      const tieneStockSuficiente = validarStockParaRegistro(
        grupo,
        registro.cantidad
      );

      if (!tieneStockSuficiente) {
        const ingredientesFaltantes = calcularIngredientesPorRegistro(
          grupo,
          registro.cantidad
        ).filter((ing) => !ing.suficiente_para_registro);

        // Mostrar modal de advertencia
        const grupoConError = {
          ...grupo,
          ingredientesTotales: ingredientesFaltantes.map((ing) => ({
            ingrediente_id: ing.ingrediente_id,
            nombre_ingrediente: ing.nombre_ingrediente,
            cantidad_por_porcion: ing.cantidad_por_porcion,
            unidad: ing.unidad,
            stock_disponible: ing.stock_disponible,
            cantidad_total_necesaria: ing.cantidad_necesaria_registro,
            suficiente: false,
          })),
        };
        setGrupoConIngredientesInsuficientes(grupoConError);
        setMostrarModalAdvertencia(true);
        return;
      }
    }

    await actualizarEstadoRegistro(registro.id, proximoEstado);
    setTabActivo(tabParaEstado(proximoEstado));
  };

  /**
   * Manejar acción masiva (Iniciar/Terminar) sobre todo el grupo o
   * sobre los registros seleccionados.
   *
   * IMPORTANTE (TD-043): NO usar el endpoint `actualizarEstadoMasivo`
   * del backend (descuenta stock agregando todos los registros en un
   * sólo movimiento con `cocina_registro_id NULL` → no es reversible
   * granularmente). En su lugar ITERAMOS el cambio de estado
   * individual por cada registro del grupo, igual que el botón
   * granular: cada llamada genera su propio movimiento trazable, y
   * todos quedan reversibles vía /revertir.
   *
   * Estrategia: Promise.allSettled para que un registro con stock
   * insuficiente no aborte al resto; al final mostramos un resumen.
   */
  const manejarActualizacionMasiva = async (
    grupo: GrupoReceta,
    nuevoEstado: 'pendiente' | 'en_preparacion' | 'terminado'
  ) => {
    const grupoId = grupo.receta_id;
    const seleccionados = registrosSeleccionados[grupoId];

    // Estado origen esperado por destino (el endpoint individual
    // updateEstado dispara descuento sólo en pendiente → en_preparacion;
    // los demás cambios son sólo estado).
    let estadoOrigen: string | null = null;
    if (nuevoEstado === 'en_preparacion') estadoOrigen = 'pendiente';
    else if (nuevoEstado === 'terminado') estadoOrigen = 'en_preparacion';

    // Candidatos a actualizar: filtrar por estado origen + por seleccion
    // (si la hay).
    const idsSeleccionados =
      seleccionados && seleccionados.size > 0
        ? new Set<number>(Array.from(seleccionados))
        : null;
    const candidatos = grupo.registros.filter((r) => {
      if (idsSeleccionados && !idsSeleccionados.has(r.id)) return false;
      if (estadoOrigen && r.estado !== estadoOrigen) return false;
      return true;
    });
    if (candidatos.length === 0) return;

    // Si se va a iniciar y la receta tiene insuficientes ingredientes
    // (validación agregada del grupo), mostrar el modal antes de pegar
    // requests inútiles.
    if (nuevoEstado === 'en_preparacion') {
      const insuficientes =
        grupo.ingredientesTotales &&
        grupo.ingredientesTotales.some((ing) => !ing.suficiente);
      if (insuficientes) {
        setGrupoConIngredientesInsuficientes(grupo);
        setMostrarModalAdvertencia(true);
        return;
      }
    }

    // SERIAL — NO PARALELO.
    // Fix 2026-06-24: antes este bloque usaba Promise.allSettled
    // (paralelo). En grupos con >1 registro, los descuentos de stock
    // se pisaban: cada llamada leía `ingredientes.stock_actual` (sin
    // FOR UPDATE) antes de que la otra escribiera → stock_anterior
    // duplicado en movimientos_stock y stock_actual final DESCONTADO
    // SOLO UNA VEZ aunque hubiera N descuentos. Caso reproducido:
    // grupo de 2 cocina_registros con la misma receta, el stock real
    // bajó solo el equivalente a 1 de los 2. Comportamiento
    // "decrementa a medias" reportado.
    // Iteramos secuencialmente para que cada commit + UPDATE termine
    // antes del siguiente. El movimiento de stock se aplica una vez
    // por cocina_registro, exactamente igual que iniciar N veces el
    // granular. Feedback final vía toast.
    const resultados: Array<
      { status: 'fulfilled' } | { status: 'rejected'; reason: unknown }
    > = [];
    // Fix tanda3 H3 (2026-07-01): pasar skipRefresh=true evita el
    // cargarDatosProduccion() por iteración. Refetch único post-batch
    // debajo.
    for (const r of candidatos) {
      try {
        await actualizarEstadoRegistro(r.id, nuevoEstado, undefined, true);
        resultados.push({ status: 'fulfilled' });
      } catch (e) {
        resultados.push({ status: 'rejected', reason: e });
      }
    }
    // Fix P0 (2026-07-02): antes hacíamos `await cargarDatosProduccion`
    // aquí y congelaba (mismo bug que Deshacer). Cada iteración ya
    // aplicó update in-place vía `actualizarEstadoRegistro` con
    // skipRefresh=true (el hook actualiza el estado local aunque
    // salte el toast individual). El stock de los ingredientes se
    // refresca en el próximo refresh natural. La serialización
    // for-await-of se mantiene por la race condition ya arreglada
    // (los descuentos de stock siguen siendo secuenciales).
    const fallidos = resultados.filter((r) => r.status === 'rejected').length;
    const okCount = candidatos.length - fallidos;
    const accionTxt = nuevoEstado === 'en_preparacion' ? 'iniciado' : nuevoEstado === 'terminado' ? 'terminado' : 'actualizado';
    // Fix tanda3 stock (2026-07-01): si TODOS los fallidos son 409
    // stock insuficiente, el hook ya toasteó el detalle por ingrediente
    // en cada iteración; no pisar con un mensaje genérico. Sólo cuando
    // haya errores mixtos (o distintos a 409) mostrar el resumen.
    const todosStockInsuficiente =
      fallidos > 0 &&
      resultados
        .filter((r): r is { status: 'rejected'; reason: unknown } => r.status === 'rejected')
        .every((r) => {
          const err = r.reason as { response?: { status?: number } };
          return err?.response?.status === 409;
        });
    if (fallidos === 0) {
      toastOk(`${okCount} registro${okCount === 1 ? '' : 's'} ${accionTxt}${okCount === 1 ? '' : 's'}`);
    } else if (todosStockInsuficiente) {
      // El toast detallado ya lo mostró el hook. No sobrescribir.
    } else if (okCount === 0) {
      toastError(`No se pudo ${accionTxt === 'iniciado' ? 'iniciar' : accionTxt} ningún registro (${fallidos} con error)`);
    } else {
      toastError(`${okCount} OK, ${fallidos} con error — revisar stock o reintentar`);
    }

    // Limpiar selección tras la acción.
    if (idsSeleccionados) {
      setRegistrosSeleccionados((prev) => ({
        ...prev,
        [grupoId]: new Set(),
      }));
    }

    // Mover el tab al estado destino si al menos uno tuvo éxito, para
    // que el usuario VEA dónde fueron los registros (ver comentario
    // del helper tabParaEstado arriba).
    if (fallidos < candidatos.length) {
      setTabActivo(tabParaEstado(nuevoEstado));
    }
  };

  /**
   * Entregar todos los registros `terminado` del grupo al despacho
   * (terminado → entregado_a_despacho). Itera el endpoint
   * /cocina/:id/transicion con comentario opcional. No toca stock.
   */
  const [modalDespacho, setModalDespacho] = useState<{
    isOpen: boolean;
    grupo: GrupoReceta | null;
  }>({ isOpen: false, grupo: null });
  const [comentarioDespacho, setComentarioDespacho] = useState<string>('');

  const abrirModalEntregarDespacho = (grupo: GrupoReceta) => {
    setComentarioDespacho('');
    setModalDespacho({ isOpen: true, grupo });
  };

  const confirmarEntregarDespacho = async () => {
    const grupo = modalDespacho.grupo;
    if (!grupo) return;
    const comentario = comentarioDespacho.trim();
    const seleccionados = registrosSeleccionados[grupo.receta_id];
    const idsSeleccionados =
      seleccionados && seleccionados.size > 0
        ? new Set<number>(Array.from(seleccionados))
        : null;
    const candidatos = grupo.registros.filter((r) => {
      if (idsSeleccionados && !idsSeleccionados.has(r.id)) return false;
      return r.estado === 'terminado';
    });
    if (candidatos.length === 0) {
      setModalDespacho({ isOpen: false, grupo: null });
      return;
    }
    const resultados = await Promise.allSettled(
      candidatos.map((r) =>
        transicionarEstadoRegistro(
          r.id,
          'entregado_a_despacho',
          comentario || undefined
        )
      )
    );
    setModalDespacho({ isOpen: false, grupo: null });
    setComentarioDespacho('');
    if (idsSeleccionados) {
      setRegistrosSeleccionados((prev) => ({
        ...prev,
        [grupo.receta_id]: new Set(),
      }));
    }
    // Mover tab a "completadas" si al menos uno tuvo éxito.
    const fallidos = resultados.filter((r) => r.status === 'rejected').length;
    if (fallidos < candidatos.length) {
      setTabActivo(tabParaEstado('entregado_a_despacho'));
    }
  };

  /**
   * Deshacer todos los registros del grupo que estén en
   * `en_preparacion` (o sólo los seleccionados). Itera el endpoint
   * individual `/cocina/:id/revertir` — devuelve stock exacto por
   * registro (net 0) y deja todos en `pendiente`.
   */
  const manejarDeshacerMasivo = async (grupo: GrupoReceta) => {
    const grupoId = grupo.receta_id;
    const seleccionados = registrosSeleccionados[grupoId];
    const idsSeleccionados =
      seleccionados && seleccionados.size > 0
        ? new Set<number>(Array.from(seleccionados))
        : null;
    const candidatos = grupo.registros.filter((r) => {
      if (idsSeleccionados && !idsSeleccionados.has(r.id)) return false;
      return r.estado === 'en_preparacion' || r.estado === 'terminado';
    });
    if (candidatos.length === 0) return;

    const cantidadPalabra =
      candidatos.length === 1
        ? '1 registro'
        : `${candidatos.length} registros`;
    if (
      !window.confirm(
        `Deshacer ${cantidadPalabra} de "${grupo.receta_nombre}"? Si están en preparación, el stock se devolverá al inventario.`
      )
    ) {
      return;
    }

    // SERIAL — mismo motivo que manejarActualizacionMasiva: el reverso
    // también toca stock (devolución) y debe ser secuencial para no
    // pisar lecturas/escrituras del ingrediente.
    const resultados: Array<
      { status: 'fulfilled' } | { status: 'rejected'; reason: unknown }
    > = [];
    // Fix tanda3 H3 (2026-07-01): pasar skipRefresh=true evita el
    // cargarDatosProduccion() por iteración (que con muchos vencidos
    // congelaba el renderer). Hacemos 1 refetch al final del batch.
    for (const r of candidatos) {
      try {
        await revertirEstadoRegistro(r.id, true);
        resultados.push({ status: 'fulfilled' });
      } catch (e) {
        resultados.push({ status: 'rejected', reason: e });
      }
    }
    // Fix P0 (2026-07-02): antes hacíamos `await cargarDatosProduccion`
    // aquí para sincronizar stock. Con tab Vencidos activo y 28+ grupos
    // ese refetch congela el hilo principal 30-60s por el rerender
    // pesado — incluso como `void` fire-and-forget, cuando la respuesta
    // vuelve `setGrupos`/`setGruposVencidos` reemplazan la lista completa
    // y el render bloquea. El update in-place del hook ya deja el estado
    // local consistente registro por registro. Sacamos el refetch: el
    // `stock_disponible` de los ingredientes se refresca en el próximo
    // refresh natural (cambio de tab/fecha o botón Refrescar).
    const fallidos = resultados.filter((r) => r.status === 'rejected').length;
    const okCount = candidatos.length - fallidos;
    if (fallidos === 0) {
      toastOk(`${okCount} registro${okCount === 1 ? '' : 's'} deshecho${okCount === 1 ? '' : 's'}`);
    } else if (okCount === 0) {
      toastError(`No se pudo deshacer ningún registro (${fallidos} con error)`);
    } else {
      toastError(`${okCount} OK, ${fallidos} con error al deshacer`);
    }

    if (idsSeleccionados) {
      setRegistrosSeleccionados((prev) => ({
        ...prev,
        [grupoId]: new Set(),
      }));
    }
    // Deshacer devuelve a 'pendiente' (o 'en_preparacion' si venia de
    // 'terminado'). El revertir individual maneja ambos; movemos el
    // tab a "pendientes" para mostrar el resultado masivo.
    if (fallidos < candidatos.length) {
      setTabActivo('pendientes');
    }
  };

  // Obtener fechas para el selector.
  // Por diseño, `fecha_preparacion = fecha_entrega - 1 día`
  // (cocina prepara D-1). Si solo ofrecemos días futuros [hoy..+6],
  // un pedido cuya entrega es HOY queda con fecha_prep=AYER y el
  // chef no lo encuentra. Incluimos 2 días previos para cubrir ese
  // caso (anteayer y ayer) sin afectar el resto del flujo.
  const obtenerFechasDisponibles = () => {
    const fechas = [];
    for (let i = -2; i < 7; i++) {
      fechas.push(cocinaService.obtenerFechaPreparacion(i));
    }
    return fechas;
  };

  // Helper para formatear fechas de manera consistente.
  // Fix tanda3 H1 (2026-07-01): tolera input null/undefined/formato
  // inesperado. Antes, `split('-').map(Number)` sobre valores como
  // undefined daba `[NaN, NaN, NaN]` → `new Date(NaN, ...)` → "Invalid
  // Date" en el detalle del pedido de Cocina. Ahora devolvemos '—'
  // para inputs no parseables (mismo criterio que dateUtils.ts).
  const formatearFechaParaMostrar = (
    fechaString: string | null | undefined,
    opciones?: Intl.DateTimeFormatOptions
  ) => {
    if (!fechaString || typeof fechaString !== 'string') return '—';
    const soloFecha = fechaString.includes('T')
      ? fechaString.split('T')[0]
      : fechaString;
    const partes = soloFecha.split('-');
    if (partes.length < 3) return '—';
    const [año, mes, día] = partes.map(Number);
    if (![año, mes, día].every((n) => Number.isFinite(n))) return '—';
    const fecha = new Date(año, mes - 1, día);
    if (isNaN(fecha.getTime())) return '—';

    return fecha.toLocaleDateString(
      'es-ES',
      opciones || {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      }
    );
  };

  /**
   * Agrupa los grupos por receta en "platos" para visualización jerárquica.
   * Para tipos combinados (3,5,6,7,10) la minuta asigna fondo + guarnición
   * en la misma celda y `grupo.pareja` (calculada en el backend) referencia
   * a la otra receta del par. Acá juntamos los dos grupos del par en una
   * sola entrada con `plato_nombre = "{Fondo} con {Guarnición}"`.
   *
   * Casos:
   *  - Ambos componentes presentes en la lista filtrada → rowSpan=2.
   *  - Solo un componente presente (su par está en otro tab/estado o
   *    fuera de la sección por filtro) → mantenemos `plato_compuesto`
   *    como nombre y `esCompuesto=true` con rowSpan=1. Antes esto
   *    caía al fallback "simple" y la receta perdía la identidad de
   *    plato compuesto al pasar a en_preparacion.
   *  - Sin pareja (plato simple, no combinado) → nombre de la receta.
   *
   * El campo `esCompuesto` se exporta explícito para que el render
   * decida mostrar el badge "Plato compuesto" sin asumir el conteo de
   * componentes.
   *
   * Mantiene el orden de los grupos: el primero del par determina la
   * posición. SOLO visualización — no muta el dataset original.
   */
  type PlatoAgrupado = {
    plato_nombre: string;
    componentes: GrupoReceta[];
    esCompuesto: boolean;
  };

  const agruparEnPlatos = (gruposSeccion: GrupoReceta[]): PlatoAgrupado[] => {
    const platos: PlatoAgrupado[] = [];
    const usados = new Set<number>();
    for (const grupo of gruposSeccion) {
      if (usados.has(grupo.receta_id)) continue;
      if (grupo.pareja) {
        const pareja = gruposSeccion.find(
          (g) => g.receta_id === grupo.pareja!.receta_id
        );
        if (pareja && !usados.has(pareja.receta_id)) {
          // Ambos componentes presentes → agrupar con rowSpan=2.
          const [fondo, guarnicion] =
            grupo.pareja.rol_propio === 'fondo' ? [grupo, pareja] : [pareja, grupo];
          platos.push({
            plato_nombre: grupo.pareja.plato_completo,
            componentes: [fondo, guarnicion],
            esCompuesto: true,
          });
          usados.add(fondo.receta_id);
          usados.add(guarnicion.receta_id);
          continue;
        }
        // Pareja no presente en la sección (otro tab / estado distinto):
        // igual mantenemos el nombre del plato compuesto en la columna
        // "Plato" — la receta no debe perder su identidad por estar sola.
        platos.push({
          plato_nombre: grupo.pareja.plato_completo,
          componentes: [grupo],
          esCompuesto: true,
        });
        usados.add(grupo.receta_id);
        continue;
      }
      // Plato simple (sin pareja calculada por el backend).
      platos.push({
        plato_nombre: grupo.receta_nombre,
        componentes: [grupo],
        esCompuesto: false,
      });
      usados.add(grupo.receta_id);
    }
    return platos;
  };

  const agruparPorFechaEntrega = (gruposCocina: any[]) => {
    // Usar cocinaService para obtener fechas locales consistentes
    const fechaHoy = cocinaService.obtenerFechaPreparacion(0); // hoy
    const fechaMañana = cocinaService.obtenerFechaPreparacion(1); // mañana
    const fechaPasadoMañana = cocinaService.obtenerFechaPreparacion(2); // pasado mañana

    const grupoPorFecha = {
      hoy: [] as any[],
      mañana: [] as any[],
      pasadoMañana: [] as any[],
      otras: [] as any[],
    };

    gruposCocina.forEach((grupo) => {
      // Agrupar por fecha de PREPARACIÓN (cuando se cocina), no por fecha de entrega
      const fechaPreparacion = grupo.fecha_preparacion;

      if (fechaPreparacion === fechaHoy) {
        grupoPorFecha.hoy.push(grupo);
      } else if (fechaPreparacion === fechaMañana) {
        grupoPorFecha.mañana.push(grupo);
      } else if (fechaPreparacion === fechaPasadoMañana) {
        grupoPorFecha.pasadoMañana.push(grupo);
      } else {
        grupoPorFecha.otras.push(grupo);
      }
    });

    return [
      {
        titulo: 'PREPARAR HOY',
        fecha: fechaHoy,
        grupos: grupoPorFecha.hoy,
        colorTitulo:
          'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700',
        colorHeader:
          'bg-red-100 dark:bg-red-800/30 border-red-300 dark:border-red-600',
      },
      {
        titulo: 'PREPARAR MAÑANA',
        fecha: fechaMañana,
        grupos: grupoPorFecha.mañana,
        colorTitulo:
          'text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-700',
        colorHeader:
          'bg-orange-100 dark:bg-orange-800/30 border-orange-300 dark:border-orange-600',
      },
      {
        titulo: 'PREPARAR PASADO MAÑANA',
        fecha: fechaPasadoMañana,
        grupos: grupoPorFecha.pasadoMañana,
        colorTitulo:
          'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700',
        colorHeader:
          'bg-blue-100 dark:bg-blue-800/30 border-blue-300 dark:border-blue-600',
      },
      {
        titulo: 'OTRAS FECHAS DE PREPARACIÓN',
        fecha: '',
        grupos: grupoPorFecha.otras,
        colorTitulo:
          'text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-700',
        colorHeader:
          'bg-gray-100 dark:bg-gray-800/30 border-gray-300 dark:border-gray-600',
      },
    ].filter((seccion) => seccion.grupos.length > 0);
  };

  const obtenerIconoEstado = (estado: string) => {
    switch (estado) {
      case 'pendiente':
        return '⏳';
      case 'en_preparacion':
        return '🔄';
      case 'terminado':
        return '✅';
      default:
        return '❓';
    }
  };

  const obtenerColorEstado = (estado: string): string => {
    switch (estado) {
      case 'pendiente':
        return 'text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20';
      case 'en_preparacion':
        return 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20';
      case 'terminado':
        return 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20';
      default:
        return 'text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800';
    }
  };

  // Filtrar grupos según pestaña activa. La pestaña 'vencidos' usa una
  // lista distinta (gruposVencidos) cargada desde /cocina/pedidos-vencidos.
  const gruposFiltrados = React.useMemo(() => {
    if (tabActivo === 'vencidos') return gruposVencidos;
    if (tabActivo === 'todas') return grupos;

    return grupos.filter((grupo) => {
      const { pendiente, en_preparacion, terminado } = grupo.resumen_estados;

      switch (tabActivo) {
        case 'pendientes':
          return pendiente > 0; // Tiene registros pendientes
        case 'en_preparacion':
          return en_preparacion > 0; // Tiene registros en preparación
        case 'completadas':
          return pendiente === 0 && en_preparacion === 0 && terminado > 0; // Todo terminado
        default:
          return true;
      }
    });
  }, [grupos, gruposVencidos, tabActivo]);

  // Fix congelamiento P0 (2026-07-03): memoizar la doble agrupación
  // (por fecha × por plato). Antes se recalculaba en cada render, y en
  // tab Vencidos con 28 grupos × subárbol de detalles el costo era
  // suficiente para que un solo setState del hook congelara el hilo
  // 9-60s. Con esta memo el render solo recorre cuando cambia
  // `gruposFiltrados` — que es el caso operativo (después de un
  // Iniciar/Terminar/Deshacer/A-despacho local).
  const seccionesConPlatos = React.useMemo(
    () =>
      agruparPorFechaEntrega(gruposFiltrados).map((seccion) => ({
        ...seccion,
        platos: agruparEnPlatos(seccion.grupos),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [gruposFiltrados]
  );

  // Calcular contadores para las pestañas
  const contadores = React.useMemo(() => {
    return {
      pendientes: grupos.filter((g) => g.resumen_estados.pendiente > 0).length,
      en_preparacion: grupos.filter((g) => g.resumen_estados.en_preparacion > 0)
        .length,
      completadas: grupos.filter(
        (g) =>
          g.resumen_estados.pendiente === 0 &&
          g.resumen_estados.en_preparacion === 0 &&
          g.resumen_estados.terminado > 0
      ).length,
      todas: grupos.length,
      vencidos: gruposVencidos.length,
    };
  }, [grupos, gruposVencidos]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <PageHeader
        title="Cocina - Control de Producción"
        helpSeccion="cocina"
        subtitle="Gestión granular de preparación por pedido y receta"
        actions={
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              <select
                value={fechaPreparacion}
                onChange={(e) => cambiarFechaPreparacion(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              >
                {obtenerFechasDisponibles().map((fecha) => (
                  <option key={fecha} value={fecha}>
                    {formatearFechaParaMostrar(fecha)}
                  </option>
                ))}
              </select>
            </div>
            <button
              onClick={() => cargarDatosProduccion(fechaPreparacion)}
              disabled={loading}
              className="inline-flex items-center px-4 py-2 bg-blue-600 dark:bg-blue-700 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
            >
              <RefreshCw
                className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`}
              />
              {loading ? 'Cargando...' : 'Actualizar'}
            </button>
          </div>
        }
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Vista por segmento (Fase 5, 2026-06-19). Solo aparece
            cuando el día tiene pedidos con segmento día/noche desde
            una operación con flag prendido. Con flag apagado en todas
            las operaciones (estado normal), `vistaPorSegmento` es null
            y la pantalla queda igual que hoy. */}
        {vistaPorSegmento && (
          <VistaCocinaSegmentada vista={vistaPorSegmento} />
        )}

        {/* Estados de error y carga */}
        {error && (
          <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <p className="text-red-800 dark:text-red-200">❌ Error: {error}</p>
          </div>
        )}

        {loading && (
          <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-8 text-center">
            <div className="animate-spin text-3xl mb-2">🔄</div>
            <p className="text-blue-800 dark:text-blue-200">
              Cargando datos de producción...
            </p>
          </div>
        )}

        {/* Sin datos: ni del día seleccionado ni vencidos */}
        {!loading && grupos.length === 0 && gruposVencidos.length === 0 && (
          <div className="mb-6 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-8 text-center">
            <p className="text-gray-600 dark:text-gray-400">
              📭 No hay recetas programadas para preparar el{' '}
              {formatearFechaParaMostrar(fechaPreparacion)}
            </p>
          </div>
        )}

        {/* Vista de tabla de producción */}
        {!loading && (grupos.length > 0 || gruposVencidos.length > 0) && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            {/* Header de la tabla */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                📋 Lista de Producción -{' '}
                {formatearFechaParaMostrar(fechaPreparacion)}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {grupos.length} recetas •{' '}
                {grupos.reduce(
                  (total, grupo) => total + grupo.total_registros,
                  0
                )}{' '}
                pedidos
              </p>
            </div>

            {/* Pestañas de filtrado */}
            <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => setTabActivo('pendientes')}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    tabActivo === 'pendientes'
                      ? 'bg-orange-600 dark:bg-orange-600 text-white shadow-sm'
                      : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 border border-gray-300 dark:border-gray-600'
                  }`}
                >
                  <span>⏳</span>
                  <span>Pendientes</span>
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                      tabActivo === 'pendientes'
                        ? 'bg-orange-700 dark:bg-orange-700 text-white'
                        : 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300'
                    }`}
                  >
                    {contadores.pendientes}
                  </span>
                </button>

                <button
                  onClick={() => setTabActivo('en_preparacion')}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    tabActivo === 'en_preparacion'
                      ? 'bg-blue-600 dark:bg-blue-600 text-white shadow-sm'
                      : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 border border-gray-300 dark:border-gray-600'
                  }`}
                >
                  <span>🔄</span>
                  <span>En Preparación</span>
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                      tabActivo === 'en_preparacion'
                        ? 'bg-blue-700 dark:bg-blue-700 text-white'
                        : 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'
                    }`}
                  >
                    {contadores.en_preparacion}
                  </span>
                </button>

                <button
                  onClick={() => setTabActivo('completadas')}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    tabActivo === 'completadas'
                      ? 'bg-green-600 dark:bg-green-600 text-white shadow-sm'
                      : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 border border-gray-300 dark:border-gray-600'
                  }`}
                >
                  <span>✅</span>
                  <span>Completadas</span>
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                      tabActivo === 'completadas'
                        ? 'bg-green-700 dark:bg-green-700 text-white'
                        : 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'
                    }`}
                  >
                    {contadores.completadas}
                  </span>
                </button>

                <button
                  onClick={() => setTabActivo('todas')}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    tabActivo === 'todas'
                      ? 'bg-gray-600 dark:bg-gray-600 text-white shadow-sm'
                      : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 border border-gray-300 dark:border-gray-600'
                  }`}
                >
                  <span>📊</span>
                  <span>Todas</span>
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                      tabActivo === 'todas'
                        ? 'bg-gray-700 dark:bg-gray-700 text-white'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {contadores.todas}
                  </span>
                </button>

                {/* Tab Vencidos: pedidos con fecha_entrega < hoy. Badge
                    rojo cuando hay vencidos para que se note. Vienen
                    del endpoint /cocina/pedidos-vencidos. */}
                <button
                  onClick={() => setTabActivo('vencidos')}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    tabActivo === 'vencidos'
                      ? 'bg-red-600 dark:bg-red-700 text-white shadow-sm'
                      : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-red-50 dark:hover:bg-red-900/20 border border-gray-300 dark:border-gray-600 hover:border-red-300 dark:hover:border-red-700'
                  }`}
                  title="Pedidos con fecha de entrega ya pasada"
                >
                  <span>⚠️</span>
                  <span>Vencidos</span>
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                      tabActivo === 'vencidos'
                        ? 'bg-red-800 dark:bg-red-800 text-white'
                        : 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300'
                    }`}
                  >
                    {contadores.vencidos}
                  </span>
                </button>
              </div>
            </div>

            {/* Tabla responsive: scroll horizontal sólo en pantallas <lg
                (≤1023px). En desktop (≥lg) entra completa, con
                `table-fixed` para que respete los anchos por columna y
                no se pase del viewport. */}
            <div className="overflow-x-auto lg:overflow-visible">
              <table className="w-full table-fixed divide-y divide-gray-200 dark:divide-gray-700">
                <colgroup>
                  <col className="w-[20%]" />
                  <col className="w-[20%]" />
                  <col className="w-[16%]" />
                  <col className="w-[7%]" />
                  <col className="w-[8%]" />
                  <col className="w-[9%]" />
                  <col className="w-[12%]" />
                  <col className="w-[8%]" />
                </colgroup>
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Plato
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Receta
                    </th>
                    <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Pedidos
                    </th>
                    <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Porciones
                    </th>
                    <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Stock
                    </th>
                    <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Acciones
                    </th>
                    <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Detalles
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {seccionesConPlatos.map((seccion) => (
                    <React.Fragment key={seccion.titulo}>
                      {/* Encabezado de sección */}
                      <tr className={`${seccion.colorTitulo} border-l-4`}>
                        <td colSpan={8} className="px-4 py-2">
                          <div className="flex items-center justify-between">
                            <div>
                              <h2 className="text-lg font-bold text-inherit">
                                🍳 {seccion.titulo}
                              </h2>
                              {seccion.fecha && (
                                <p className="text-sm font-normal mt-1 text-inherit opacity-80">
                                  📅 Fecha de preparación:{' '}
                                  {formatearFechaParaMostrar(seccion.fecha, {
                                    weekday: 'long',
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                  })}
                                </p>
                              )}
                            </div>
                            <div className="text-sm font-medium text-inherit opacity-90">
                              {seccion.grupos.length} receta
                              {seccion.grupos.length !== 1 ? 's' : ''} •{' '}
                              {seccion.grupos.reduce(
                                (sum, g) => sum + g.total_registros,
                                0
                              )}{' '}
                              pedido
                              {seccion.grupos.reduce(
                                (sum, g) => sum + g.total_registros,
                                0
                              ) !== 1
                                ? 's'
                                : ''}
                            </div>
                          </div>
                        </td>
                      </tr>

                      {/* Grupos de esta sección, agrupados por "plato"
                          (fondo+guarnición unidos cuando aplica) */}
                      {seccion.platos.map((plato, platoIdx) => {
                        // rowspan de la columna Plato cubre todas las filas
                        // del par: cada componente aporta 1 fila base + 1
                        // fila de detalles si está expandido.
                        const rowspan = plato.componentes.reduce(
                          (acc, g) =>
                            acc + 1 + (gruposExpandidos.has(g.receta_id) ? 1 : 0),
                          0
                        );
                        // Plato compuesto = identidad de la receta. Se
                        // marca explícitamente en agruparEnPlatos para no
                        // perderse cuando un componente queda solo en la
                        // sección (par en otro tab/estado).
                        const esCompuesto = plato.esCompuesto;
                        return (
                          <React.Fragment
                            key={`plato-${seccion.titulo}-${platoIdx}-${plato.componentes[0].receta_id}`}
                          >
                            {plato.componentes.map((grupo, componenteIdx) => {
                              const grupoId = grupo.receta_id;
                              const expandido = gruposExpandidos.has(grupoId);
                              const seleccionados =
                                registrosSeleccionados[grupoId] || new Set();
                              const esPrimeroDelPlato = componenteIdx === 0;

                              return (
                                <React.Fragment key={grupoId}>
                                  {/* Fila principal del grupo */}
                                  <tr
                                    key={grupoId}
                                    onClick={() => toggleGrupo(grupoId)}
                                    className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                                  >
                                    {/* Columna Plato (solo en la primera fila del par) */}
                                    {esPrimeroDelPlato && (
                                      <td
                                        rowSpan={rowspan}
                                        className="px-3 py-2 align-top bg-gray-50/40 dark:bg-gray-900/30 border-r border-gray-200 dark:border-gray-700"
                                      >
                                        <div className="text-sm font-semibold text-gray-900 dark:text-white leading-snug break-words">
                                          {plato.plato_nombre}
                                        </div>
                                        {esCompuesto && (
                                          <div className="mt-1 inline-flex items-center text-[10px] uppercase tracking-wider text-blue-600 dark:text-blue-400 font-semibold">
                                            Plato compuesto
                                          </div>
                                        )}
                                      </td>
                                    )}
                                    {/* Columna Receta */}
                                    <td className="px-3 py-2 align-top">
                                <div className="flex items-start gap-2">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleGrupo(grupoId);
                                    }}
                                    className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 shrink-0 mt-0.5"
                                  >
                                    {expandido ? '📖' : '📕'}
                                  </button>
                                  <div className="min-w-0">
                                    <div className="text-sm font-medium text-gray-900 dark:text-white break-words">
                                      {grupo.receta_nombre}
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400 break-words">
                                      {grupo.receta_categoria}
                                    </div>
                                  </div>
                                </div>
                              </td>

                              {/* Columna Estado */}
                              <td className="px-2 py-2 align-top">
                                <div className="flex flex-col gap-1 items-center">
                                  {grupo.resumen_estados.pendiente > 0 && (
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[11px] font-medium bg-orange-100 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 whitespace-nowrap">
                                      ⏳ {grupo.resumen_estados.pendiente} Pend.
                                    </span>
                                  )}
                                  {grupo.resumen_estados.en_preparacion > 0 && (
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[11px] font-medium bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 whitespace-nowrap">
                                      🔄 {grupo.resumen_estados.en_preparacion} En prep.
                                    </span>
                                  )}
                                  {grupo.resumen_estados.terminado > 0 && (
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[11px] font-medium bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300 whitespace-nowrap">
                                      ✅ {grupo.resumen_estados.terminado} Term.
                                    </span>
                                  )}
                                </div>
                              </td>

                              {/* Columna Pedidos */}
                              <td className="px-2 py-2 text-sm text-center text-gray-900 dark:text-white">
                                {grupo.total_registros}
                              </td>

                              {/* Columna Porciones */}
                              <td className="px-2 py-2 text-sm text-center text-gray-900 dark:text-white">
                                {grupo.cantidad_total}
                              </td>

                              {/* Columna Stock */}
                              <td className="px-2 py-2 text-center">
                                {grupo.tiene_ingredientes_faltantes ? (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[11px] font-medium bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300 whitespace-nowrap">
                                    ⚠️ Insuf.
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[11px] font-medium bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300 whitespace-nowrap">
                                    ✅ Suficiente
                                  </span>
                                )}
                              </td>

                              {/* Columna Acciones (TD-043 RESUELTA 2026-06-13):
                                  Iniciar / Deshacer / Terminar a nivel grupo
                                  ahora ITERAN los endpoints individuales
                                  (PATCH /cocina/:id/estado + POST /cocina/:id/
                                  revertir). Cada registro genera su movimiento
                                  trazable y queda reversible — no se usa el
                                  endpoint masivo agregado del backend que
                                  causaba 500/no-trazabilidad. */}
                              <td className="px-2 py-2 whitespace-nowrap text-center text-sm font-medium w-28">
                                {/* Botones apilados verticalmente (2026-06-13).
                                    Orden por flujo operativo:
                                      pendiente → Iniciar (único)
                                      en_preparacion → Deshacer arriba, Terminar abajo
                                      terminado → Deshacer (volver a en_preparacion) */}
                                <div className="flex flex-col gap-1 items-stretch">
                                  {/* Iniciar masivo (sólo cuando hay pendientes) */}
                                  {grupo.resumen_estados.pendiente > 0 && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        manejarActualizacionMasiva(
                                          grupo,
                                          'en_preparacion'
                                        );
                                      }}
                                      disabled={
                                        actualizandoEstado[`masivo-${grupoId}`]
                                      }
                                      className="px-2 py-1 bg-blue-500 dark:bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-600 dark:hover:bg-blue-500 disabled:opacity-50"
                                      title="Iniciar preparación de todos los pendientes del grupo"
                                    >
                                      🔄 Iniciar
                                    </button>
                                  )}

                                  {/* Deshacer arriba (cuando hay en_preparacion o terminado) */}
                                  {(grupo.resumen_estados.en_preparacion > 0 ||
                                    grupo.resumen_estados.terminado > 0) && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        manejarDeshacerMasivo(grupo);
                                      }}
                                      disabled={
                                        actualizandoEstado[`masivo-${grupoId}`]
                                      }
                                      className="px-2 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 rounded text-xs font-medium hover:bg-amber-200 dark:hover:bg-amber-900/50 disabled:opacity-50"
                                      title="Deshacer: vuelve los registros del grupo a su estado anterior (devuelve stock si aplica)"
                                    >
                                      ↩️ Deshacer
                                    </button>
                                  )}

                                  {/* Terminar abajo (cuando hay en_preparacion) */}
                                  {grupo.resumen_estados.en_preparacion > 0 && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        manejarActualizacionMasiva(
                                          grupo,
                                          'terminado'
                                        );
                                      }}
                                      disabled={
                                        actualizandoEstado[`masivo-${grupoId}`]
                                      }
                                      className="px-2 py-1 bg-green-500 dark:bg-green-600 text-white rounded text-xs font-medium hover:bg-green-600 dark:hover:bg-green-500 disabled:opacity-50"
                                      title="Marcar como terminado todo el grupo"
                                    >
                                      ✅ Terminar
                                    </button>
                                  )}

                                  {/* Entregar a despacho (cuando hay terminados) */}
                                  {grupo.resumen_estados.terminado > 0 && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        abrirModalEntregarDespacho(grupo);
                                      }}
                                      className="px-2 py-1 bg-[#29ABE2] hover:bg-[#1f8fbf] text-white rounded text-xs font-medium disabled:opacity-50"
                                      title="Entregar los registros terminados al módulo Despacho"
                                    >
                                      🚚 A despacho
                                    </button>
                                  )}

                                  {/* Completado: todo terminado */}
                                  {grupo.resumen_estados.pendiente === 0 &&
                                    grupo.resumen_estados.en_preparacion ===
                                      0 &&
                                    grupo.resumen_estados.terminado > 0 && (
                                      <span className="px-2 py-1 rounded text-xs font-medium bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300 text-center">
                                        ✓ Completado
                                      </span>
                                    )}
                                </div>
                              </td>

                              {/* Columna Detalles */}
                              <td className="px-2 py-2 text-center">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleGrupo(grupoId);
                                  }}
                                  className="inline-flex items-center px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-xs font-medium transition-colors whitespace-nowrap"
                                  title={
                                    expandido
                                      ? 'Contraer detalles'
                                      : 'Ver detalles de preparación'
                                  }
                                >
                                  {expandido ? '📖 Ocultar' : '📕 Detalle'}
                                </button>
                              </td>
                            </tr>

                            {/* Fila expandida con detalles */}
                            {expandido && (
                              <tr key={`${grupoId}-details`}>
                                <td
                                  colSpan={7}
                                  className="px-4 py-3 bg-gray-50 dark:bg-gray-700"
                                >
                                  <div className="space-y-4">
                                    {/* Controles de selección masiva */}
                                    {grupo.registros.length > 1 && (
                                      <div className="flex items-center gap-4 pb-3 border-b border-gray-200 dark:border-gray-600">
                                        <label className="flex items-center gap-2">
                                          <input
                                            type="checkbox"
                                            checked={
                                              grupo.registros.length > 0 &&
                                              grupo.registros.every((r: { id: any }) =>
                                                seleccionados.has(r.id)
                                              )
                                            }
                                            onChange={() =>
                                              toggleSeleccionTodoGrupo(grupo)
                                            }
                                            className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:bg-gray-700"
                                          />
                                          <span className="text-sm text-gray-700 dark:text-gray-300">
                                            Seleccionar todos (
                                            {seleccionados.size} seleccionados)
                                          </span>
                                        </label>
                                        {seleccionados.size > 0 && (
                                          <div className="flex gap-2">
                                            {/* Iniciar seleccionados — itera
                                                el endpoint individual por
                                                cada registro seleccionado
                                                (TD-043 RESUELTA). */}
                                            <button
                                              onClick={() =>
                                                manejarActualizacionMasiva(
                                                  grupo,
                                                  'en_preparacion'
                                                )
                                              }
                                              disabled={
                                                actualizandoEstado[
                                                  `masivo-${grupoId}`
                                                ]
                                              }
                                              className="px-3 py-1 bg-blue-500 dark:bg-blue-600 text-white rounded text-sm hover:bg-blue-600 dark:hover:bg-blue-500"
                                            >
                                              🔄 Iniciar seleccionados
                                            </button>
                                            <button
                                              onClick={() =>
                                                manejarDeshacerMasivo(grupo)
                                              }
                                              disabled={
                                                actualizandoEstado[
                                                  `masivo-${grupoId}`
                                                ]
                                              }
                                              className="px-3 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 rounded text-sm hover:bg-amber-200 dark:hover:bg-amber-900/50"
                                            >
                                              ↩️ Deshacer seleccionados
                                            </button>
                                            <button
                                              onClick={() =>
                                                manejarActualizacionMasiva(
                                                  grupo,
                                                  'terminado'
                                                )
                                              }
                                              disabled={
                                                actualizandoEstado[
                                                  `masivo-${grupoId}`
                                                ]
                                              }
                                              className="px-3 py-1 bg-green-500 dark:bg-green-600 text-white rounded text-sm hover:bg-green-600 dark:hover:bg-green-500"
                                            >
                                              ✅ Terminar seleccionados
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    )}

                                    {/* Pedidos vinculados con tarjetas individuales */}
                                    {grupo.registros &&
                                      grupo.registros.length > 0 && (
                                        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                                          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                                            📦 Pedidos vinculados a esta
                                            preparación:
                                          </h4>
                                          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                                            {grupo.registros.map(
                                              (registro: any) => (
                                                <div
                                                  key={registro.id}
                                                  className="bg-white dark:bg-gray-800 border-2 border-blue-200 dark:border-blue-700 rounded-lg p-4 hover:border-blue-400 dark:hover:border-blue-500 transition-colors"
                                                >
                                                  {/* Header con checkbox y estado */}
                                                  <div className="flex items-center justify-between mb-3">
                                                    <div className="flex items-center gap-2">
                                                      <input
                                                        type="checkbox"
                                                        checked={seleccionados.has(
                                                          registro.id
                                                        )}
                                                        onChange={() =>
                                                          toggleSeleccionRegistro(
                                                            grupoId,
                                                            registro.id
                                                          )
                                                        }
                                                        className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:bg-gray-700"
                                                      />
                                                      <div>
                                                        <div className="text-sm font-bold text-blue-700 dark:text-blue-300">
                                                          {
                                                            registro.pedido_codigo
                                                          }
                                                        </div>
                                                        <div className="text-xs text-gray-600 dark:text-gray-400">
                                                          {
                                                            registro.cliente_nombre
                                                          }
                                                        </div>
                                                      </div>
                                                    </div>
                                                    <span
                                                      className={`px-2 py-1 rounded text-xs font-medium ${obtenerColorEstado(registro.estado)}`}
                                                    >
                                                      {obtenerIconoEstado(
                                                        registro.estado
                                                      )}{' '}
                                                      {registro.estado}
                                                    </span>
                                                  </div>

                                                  {/* Info del pedido */}
                                                  <div className="mb-3 text-sm text-gray-600 dark:text-gray-400 space-y-1">
                                                    <div className="flex items-center gap-2">
                                                      <span>🍽️</span>
                                                      <span>
                                                        {registro.cantidad}{' '}
                                                        porciones
                                                      </span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                      <span>📅</span>
                                                      <span>
                                                        {formatearFechaParaMostrar(
                                                          grupo.fecha_entrega
                                                        )}
                                                      </span>
                                                    </div>
                                                  </div>

                                                  {/* Botones de acción */}
                                                  <div className="flex gap-2">
                                                    <button
                                                      onClick={() =>
                                                        manejarClickRegistro(
                                                          registro,
                                                          grupo
                                                        )
                                                      }
                                                      disabled={
                                                        actualizandoEstado[
                                                          `registro-${registro.id}`
                                                        ] ||
                                                        (obtenerProximoEstado(
                                                          registro.estado
                                                        ) ===
                                                          'en_preparacion' &&
                                                          !validarStockParaRegistro(
                                                            grupo,
                                                            registro.cantidad
                                                          ))
                                                      }
                                                      className={`flex-1 px-3 py-1.5 rounded text-xs font-medium ${
                                                        obtenerProximoEstado(
                                                          registro.estado
                                                        ) ===
                                                          'en_preparacion' &&
                                                        !validarStockParaRegistro(
                                                          grupo,
                                                          registro.cantidad
                                                        )
                                                          ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                                                          : obtenerEstiloBotonRegistro(
                                                              registro.estado
                                                            )
                                                      } disabled:opacity-50`}
                                                      title={
                                                        obtenerProximoEstado(
                                                          registro.estado
                                                        ) ===
                                                          'en_preparacion' &&
                                                        !validarStockParaRegistro(
                                                          grupo,
                                                          registro.cantidad
                                                        )
                                                          ? 'Stock insuficiente para este pedido'
                                                          : ''
                                                      }
                                                    >
                                                      {actualizandoEstado[
                                                        `registro-${registro.id}`
                                                      ]
                                                        ? '⏳'
                                                        : obtenerProximoEstado(
                                                              registro.estado
                                                            ) ===
                                                              'en_preparacion' &&
                                                            !validarStockParaRegistro(
                                                              grupo,
                                                              registro.cantidad
                                                            )
                                                          ? '⚠️'
                                                          : obtenerIconoProximoEstado(
                                                              registro.estado
                                                            )}{' '}
                                                      {obtenerTextoBotonRegistro(
                                                        registro.estado
                                                      )}
                                                    </button>
                                                    {/* Deshacer: vuelve a pendiente (en_preparacion)
                                                        o a en_preparacion (terminado). Bloqueado
                                                        en pendiente y despachado. Backend revierte
                                                        el descuento de stock cuando aplica. */}
                                                    {(registro.estado === 'en_preparacion' ||
                                                      registro.estado === 'terminado') && (
                                                      <button
                                                        onClick={async () => {
                                                          const msg =
                                                            registro.estado === 'en_preparacion'
                                                              ? `Volver "${registro.pedido_codigo}" a Pendiente y devolver el stock descontado al inventario?`
                                                              : `Volver "${registro.pedido_codigo}" a En Preparación?`;
                                                          if (!window.confirm(msg)) return;
                                                          try {
                                                            await revertirEstadoRegistro(
                                                              registro.id
                                                            );
                                                          } catch {
                                                            /* error ya mostrado por hook */
                                                          }
                                                        }}
                                                        disabled={
                                                          actualizandoEstado[
                                                            `revertir-${registro.id}`
                                                          ]
                                                        }
                                                        className="px-3 py-1.5 bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 rounded text-xs font-medium hover:bg-amber-200 dark:hover:bg-amber-900/50 disabled:opacity-50"
                                                        title={
                                                          registro.estado === 'en_preparacion'
                                                            ? 'Deshacer Iniciar (devuelve el stock al inventario)'
                                                            : 'Deshacer Terminar (vuelve a En Preparación, no toca stock)'
                                                        }
                                                      >
                                                        {actualizandoEstado[
                                                          `revertir-${registro.id}`
                                                        ]
                                                          ? '⏳'
                                                          : '↩️'}{' '}
                                                        Deshacer
                                                      </button>
                                                    )}
                                                    <button
                                                      onClick={() =>
                                                        verDetallePedido(
                                                          registro.pedido_id
                                                        )
                                                      }
                                                      disabled={cargandoDetalle}
                                                      className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-xs font-medium hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
                                                      title="Ver detalle completo del pedido"
                                                    >
                                                      👁️ Ver
                                                    </button>
                                                  </div>
                                                </div>
                                              )
                                            )}
                                          </div>
                                        </div>
                                      )}

                                    {/* Ingredientes necesarios */}
                                    {grupo.ingredientesTotales &&
                                      grupo.ingredientesTotales.length > 0 && (
                                        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                                          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                                            🧄 Ingredientes necesarios:
                                          </h4>
                                          <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
                                            {grupo.ingredientesTotales.map(
                                              (ingrediente: any) => (
                                                <div
                                                  key={
                                                    ingrediente.ingrediente_id
                                                  }
                                                  className={`p-3 rounded-lg border-2 text-sm ${
                                                    ingrediente.suficiente
                                                      ? 'border-green-200 dark:border-green-700 bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200'
                                                      : 'border-red-200 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200'
                                                  }`}
                                                >
                                                  <div className="font-medium">
                                                    {
                                                      ingrediente.nombre_ingrediente
                                                    }
                                                  </div>
                                                  <div>
                                                    Necesario:{' '}
                                                    {
                                                      ingrediente.cantidad_total_necesaria
                                                    }{' '}
                                                    {ingrediente.unidad}
                                                  </div>
                                                  <div>
                                                    Stock:{' '}
                                                    {
                                                      ingrediente.stock_disponible
                                                    }{' '}
                                                    {ingrediente.unidad}
                                                  </div>
                                                  {!ingrediente.suficiente && (
                                                    <div className="text-red-600 dark:text-red-400 font-medium">
                                                      ⚠️ Falta:{' '}
                                                      {ingrediente.cantidad_total_necesaria -
                                                        ingrediente.stock_disponible}{' '}
                                                      {ingrediente.unidad}
                                                    </div>
                                                  )}
                                                </div>
                                              )
                                            )}
                                          </div>
                                        </div>
                                      )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                            })}
                          </React.Fragment>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>

              {/* Mensaje cuando no hay resultados en la pestaña */}
              {gruposFiltrados.length === 0 && (
                <div className="p-8 text-center">
                  <p className="text-gray-500 dark:text-gray-400 text-sm">
                    {tabActivo === 'pendientes' && (
                      <>
                        ✅ No hay recetas pendientes. ¡Todas iniciadas o
                        completadas!
                      </>
                    )}
                    {tabActivo === 'en_preparacion' && (
                      <>📭 No hay recetas en preparación en este momento.</>
                    )}
                    {tabActivo === 'completadas' && (
                      <>📋 No hay recetas completadas todavía.</>
                    )}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modal de Advertencia de Ingredientes Insuficientes */}
      {mostrarModalAdvertencia && grupoConIngredientesInsuficientes && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center">
                <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Ingredientes Insuficientes
                </h3>
              </div>
              <button
                onClick={() => setMostrarModalAdvertencia(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="mb-4">
                <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed mb-2">
                  No se puede iniciar la producción de{' '}
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {grupoConIngredientesInsuficientes.receta_nombre}
                  </span>{' '}
                  porque faltan los siguientes ingredientes:
                </p>
              </div>

              {/* Lista de ingredientes faltantes */}
              <div className="space-y-3 mb-6">
                {grupoConIngredientesInsuficientes.ingredientesTotales
                  ?.filter((ing) => !ing.suficiente)
                  .map((ingrediente) => (
                    <div
                      key={ingrediente.ingrediente_id}
                      className="border border-red-200 dark:border-red-800 rounded-lg p-4 bg-red-50 dark:bg-red-900/20"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                            {ingrediente.nombre_ingrediente}
                          </h4>
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                              <span className="text-gray-600 dark:text-gray-400">
                                Disponible:
                              </span>
                              <span className="ml-2 font-medium text-gray-900 dark:text-white">
                                {ingrediente.stock_disponible.toFixed(2)}{' '}
                                {ingrediente.unidad}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-600 dark:text-gray-400">
                                Necesario:
                              </span>
                              <span className="ml-2 font-medium text-red-600 dark:text-red-400">
                                {ingrediente.cantidad_total_necesaria.toFixed(
                                  2
                                )}{' '}
                                {ingrediente.unidad}
                              </span>
                            </div>
                            <div className="col-span-2">
                              <span className="text-gray-600 dark:text-gray-400">
                                Faltante:
                              </span>
                              <span className="ml-2 font-semibold text-red-600 dark:text-red-400">
                                {(
                                  ingrediente.cantidad_total_necesaria -
                                  ingrediente.stock_disponible
                                ).toFixed(2)}{' '}
                                {ingrediente.unidad}
                              </span>
                            </div>
                          </div>
                        </div>
                        <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 ml-3 flex-shrink-0" />
                      </div>
                    </div>
                  ))}
              </div>

              {/* Información adicional */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-blue-600 dark:text-blue-400"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3 flex-1">
                    <h4 className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-1">
                      ¿Qué debes hacer?
                    </h4>
                    <p className="text-sm text-blue-700 dark:text-blue-400">
                      Antes de iniciar esta receta, debes:
                    </p>
                    <ul className="mt-2 text-sm text-blue-700 dark:text-blue-400 list-disc list-inside space-y-1">
                      <li>Comprar los ingredientes faltantes</li>
                      <li>Actualizar el stock en el sistema</li>
                      <li>
                        Verificar que todos los ingredientes estén disponibles
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Resumen de producción */}
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-6">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                  Resumen de Producción
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">
                      Receta:
                    </span>
                    <span className="ml-2 font-medium text-gray-900 dark:text-white">
                      {grupoConIngredientesInsuficientes.receta_nombre}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">
                      Cantidad Total:
                    </span>
                    <span className="ml-2 font-medium text-gray-900 dark:text-white">
                      {grupoConIngredientesInsuficientes.cantidad_total}{' '}
                      porciones
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">
                      Pedidos:
                    </span>
                    <span className="ml-2 font-medium text-gray-900 dark:text-white">
                      {grupoConIngredientesInsuficientes.total_registros}{' '}
                      pedidos
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">
                      Ingredientes Faltantes:
                    </span>
                    <span className="ml-2 font-semibold text-red-600 dark:text-red-400">
                      {
                        grupoConIngredientesInsuficientes.ingredientesTotales?.filter(
                          (ing) => !ing.suficiente
                        ).length
                      }
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-700 rounded-b-lg">
              <button
                onClick={() => setMostrarModalAdvertencia(false)}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-md text-sm font-medium transition-colors"
              >
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Detalle de Pedido */}
      {mostrarDetallePedido && pedidoDetalle && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                📦 Detalle del Pedido{' '}
                {pedidoDetalle.codigo ||
                  `P-${String(pedidoDetalle.id).padStart(4, '0')}`}
              </h3>
              <button
                onClick={() => setMostrarDetallePedido(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>

            {/* Contenido del Modal */}
            <div className="space-y-6">
              {/* Información Principal */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                    Cliente
                  </h4>
                  <p className="text-gray-700 dark:text-gray-300">
                    {pedidoDetalle.cliente?.nombre || 'N/A'}
                  </p>
                  {pedidoDetalle.cliente?.email && (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {pedidoDetalle.cliente.email}
                    </p>
                  )}
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                    Minuta
                  </h4>
                  <p className="text-gray-700 dark:text-gray-300">
                    {pedidoDetalle.minuta?.nombre || 'N/A'}
                  </p>
                  {pedidoDetalle.minuta?.descripcion && (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {pedidoDetalle.minuta.descripcion}
                    </p>
                  )}
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                    Fecha de Entrega
                  </h4>
                  <p className="text-gray-700 dark:text-gray-300">
                    {/* Helper compartido: evita el desfase TZ que
                        sufría new Date(fechaISO).toLocaleDateString
                        cuando el backend envía DATE como medianoche
                        UTC y Chile (UTC-4) mostraba el día anterior. */}
                    {formatearFechaLarga(pedidoDetalle.fecha_entrega)}
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                    Estado
                  </h4>
                  <span
                    className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                      pedidoDetalle.estado === 'solicitado'
                        ? 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200'
                        : pedidoDetalle.estado === 'en_preparacion'
                          ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200'
                          : pedidoDetalle.estado === 'listo'
                            ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-200'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                    }`}
                  >
                    {pedidoDetalle.estado?.replace('_', ' ').toUpperCase() ||
                      'SIN ESTADO'}
                  </span>
                </div>
              </div>

              {/* Observaciones */}
              {pedidoDetalle.observaciones && (
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                    Observaciones
                  </h4>
                  <p className="text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                    {pedidoDetalle.observaciones}
                  </p>
                </div>
              )}

              {/* Detalles del Pedido (Recetas) */}
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-3">
                  Detalles del Pedido
                </h4>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                    <thead className="bg-gray-100 dark:bg-gray-700">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                          Receta
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                          Cantidad
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                      {pedidoDetalle.detalles?.map((detalle: any) => (
                        <tr
                          key={detalle.id}
                          className="hover:bg-gray-50 dark:hover:bg-gray-700"
                        >
                          <td className="px-4 py-3">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {detalle.receta?.nombre || 'N/A'}
                            </div>
                            {detalle.receta?.descripcion && (
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {detalle.receta.descripcion}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center text-sm text-gray-900 dark:text-white font-medium">
                            {detalle.cantidad} porcion
                            {detalle.cantidad !== 1 ? 'es' : ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setMostrarDetallePedido(false)}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-lg text-sm font-medium transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Entregar a despacho con comentario opcional */}
      {modalDespacho.isOpen && modalDespacho.grupo && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6 space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                🚚 Entregar a despacho
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {modalDespacho.grupo.receta_nombre}
              </p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Comentario (opcional)
              </label>
              <textarea
                value={comentarioDespacho}
                onChange={(e) => setComentarioDespacho(e.target.value)}
                placeholder="Anotaciones para Despacho — opcional"
                rows={3}
                maxLength={1000}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-[#29ABE2] focus:border-[#29ABE2]"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() =>
                  setModalDespacho({ isOpen: false, grupo: null })
                }
                className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Cancelar
              </button>
              <button
                onClick={confirmarEntregarDespacho}
                className="px-4 py-2 bg-[#29ABE2] hover:bg-[#1f8fbf] text-white rounded text-sm font-semibold"
              >
                Entregar a despacho
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
